/*
 * The first-run tour: who sees it and where its card goes. Kept free of React
 * so the rules are tested on their own.
 *
 * Who: a member whose sign-in just created their account (the auth API says
 * `new_member`), on the device they signed up on, while they are on the free
 * plan. Paid members already know their way around, so a member who upgrades
 * before taking it never sees it. Finishing or skipping ends it for good.
 * `?tour=preview` shows it to anyone, for checking the tour itself.
 */

const local = () => { try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; } };

export const tourKey = (userId) => `lxops_tour_${userId}`;

const read = (storage, k) => { try { return storage?.getItem(k) ?? null; } catch { return null; } };
const write = (storage, k, v) => { try { storage?.setItem(k, v); } catch { /* private mode: no tour is fine */ } };

/** At sign-in: a brand-new account gets the tour queued. Returns the user without the flag. */
export function noteNewMember(userData, storage = local()) {
  if (!userData || typeof userData !== 'object') return userData;
  const { new_member: isNew, ...user } = userData;
  if (isNew && user.id != null && read(storage, tourKey(user.id)) == null) write(storage, tourKey(user.id), 'pending');
  return user;
}

/** 'preview' | 'tour' | null */
export function tourMode({ user, isPaid, shadow, search = '', storage = local() }) {
  if (!user) return null;
  if (new URLSearchParams(search).get('tour') === 'preview') return 'preview';
  if (isPaid || shadow) return null;
  return read(storage, tourKey(user.id)) === 'pending' ? 'tour' : null;
}

/** A paid member never takes it; a queued tour is retired so a later lapse does not bring it back. */
export function retireForPaid({ user, isPaid, storage = local() }) {
  if (isPaid && user?.id != null && read(storage, tourKey(user.id)) === 'pending') write(storage, tourKey(user.id), 'skipped');
}

/** Finished or skipped: never again on this device. A preview leaves no trace. */
export function endTour(user, mode, outcome, storage = local()) {
  if (mode === 'tour' && user?.id != null) write(storage, tourKey(user.id), outcome === 'completed' ? 'done' : 'skipped');
}

/** A step's target, if it is on screen now; null means "show the card in the middle". */
export function visibleRect(el, viewport) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  const top = Math.max(r.top, 0);
  const bottom = Math.min(r.bottom, viewport.h);
  const left = Math.max(r.left, 0);
  const right = Math.min(r.right, viewport.w);
  if (r.width <= 0 || r.height <= 0 || bottom - top < 8 || right - left < 8) return null;
  return { top, left, width: right - left, height: bottom - top };
}

/**
 * Where the card goes next to its target: below it, else above it; beside a
 * tall target (the sidebar). Always inside the screen, 16px from its edges.
 */
export function placeCard(rect, card, viewport, gap = 14, gutter = 16) {
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));
  const maxLeft = viewport.w - card.w - gutter;
  const maxTop = viewport.h - card.h - gutter;
  if (!rect) return { side: 'center', top: clamp((viewport.h - card.h) / 2, gutter, maxTop), left: clamp((viewport.w - card.w) / 2, gutter, maxLeft) };
  if (rect.height > viewport.h * 0.5 && rect.left + rect.width + gap + card.w + gutter <= viewport.w) {
    return { side: 'right', top: clamp(rect.top + 80, gutter, maxTop), left: rect.left + rect.width + gap };
  }
  const left = clamp(rect.left + rect.width / 2 - card.w / 2, gutter, maxLeft);
  if (rect.top + rect.height + gap + card.h + gutter <= viewport.h) return { side: 'below', top: rect.top + rect.height + gap, left };
  return { side: 'above', top: clamp(rect.top - gap - card.h, gutter, maxTop), left };
}
